import { useEffect, useState } from "react";
import { styled, vh } from "~/ui/style/stitches.config";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import { Button } from "../components/Button";
import { StyledProfilePic } from "../components/ProfilePic";
import { imageUrl } from "~/utils/imageurl";

export const RouletteOverlay = ({ 
  isRouletteRoom, 
  otherConnectionIds, 
  matchedDisplayName,
  matchedProfilePic,
  audioLevel 
}: {
  isRouletteRoom: boolean;
  otherConnectionIds: number[];
  matchedDisplayName?: string;
  matchedProfilePic?: string;
  audioLevel: number;
}) => {
  const [showMatchPopup, setShowMatchPopup] = useState(false);
  const [showExitSurvey, setShowExitSurvey] = useState(false);
  const [showMicCheck, setShowMicCheck] = useState(false);
  const { showMicCheck: globalShowMicCheck } = useSnapshot(localState);

  useEffect(() => {
    if (globalShowMicCheck) {
      setShowMicCheck(true);
      localState.showMicCheck = false; // Reset the trigger
    }
  }, [globalShowMicCheck]);

  const [showWelcome, setShowWelcome] = useState(false);
  const inDarkMode = useSnapshot(localState).inDarkMode;

  // Handle match found - when someone joins
  useEffect(() => {
    if (isRouletteRoom && otherConnectionIds.length > 0 && matchedDisplayName) {
      setShowWelcome(true); // Show welcome when matched
    }
  }, [isRouletteRoom, otherConnectionIds.length, matchedDisplayName]);

  // Handle participant leave - when someone leaves
  useEffect(() => {
    if (isRouletteRoom && otherConnectionIds.length === 0) {
      setShowWelcome(false); // Close welcome overlay when partner leaves
      if (!showWelcome) {
        setShowExitSurvey(true);
      }
    }
  }, [isRouletteRoom, otherConnectionIds.length, showWelcome]);

  return (
    <>
      {/* Mic check popup */}
      {showMicCheck && (
        <StyledRouletteModal 
          Theme={inDarkMode ? "dark" : "light"}
          style={{ pointerEvents: "auto" }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowMicCheck(false);
            }
          }}
        >
          <div className="modal">
            <div className="title">Talk to check mic</div>
            <RouletteMicCheck audioLevel={audioLevel} />
            <Button
              lightweight
              Theme={inDarkMode ? "dark" : "light"}
              style={{
                height: "32px",
                fontSize: "12px",
                marginTop: "16px",
              }}
              css={inDarkMode ? {
                "&:hover": {
                  backgroundColor: "$gray700 !important"
                }
              } : {}}
              onClick={() => setShowMicCheck(false)}
            >
              Ready!
            </Button>
          </div>
        </StyledRouletteModal>
      )}

      {/* Welcome popup */}
      {showWelcome && (
        <StyledRouletteModal Theme={inDarkMode ? "dark" : "light"}>
          <div className="modal">
            {matchedProfilePic && (
              <StyledProfilePic
                css={{ $$size: "68px", $$padding: "0px", marginBottom: "12px" }}
                src={imageUrl(`/profile-pics/${matchedProfilePic}`, 120)}
              />
            )}
            <div className="title" style={{ fontWeight: "400" }}>Your match is <span style={{ fontWeight: "600" }}>{matchedDisplayName}</span>!</div>
            <div className="subtitle">
              Take turns giving each other a tarot reading in your own style.
            </div>
            <Button
              lightweight
              Theme={inDarkMode ? "dark" : "light"}
              style={{
                height: "32px",
                width: "150px",
                fontSize: "12px",
                marginTop: "2px",
              }}
              css={inDarkMode ? {
                "&:hover": {
                  backgroundColor: "$gray700 !important"
                }
              } : {}}
              onClick={() => setShowWelcome(false)}
            >
              Okay!
            </Button>
          </div>
        </StyledRouletteModal>
      )}
    </>
  );
};


const RouletteMicCheck = ({ audioLevel }: { audioLevel: number }) => {
  const inDarkMode = useSnapshot(localState).inDarkMode;
  
  return (
    <div style={{ marginTop: "10px" }}>
      <div style={{
        height: "17px",
        background: inDarkMode ? "#000" : "#fff",
        borderRadius: "4px",
        overflow: "hidden",
      }}>
        <div style={{
          height: "100%",
          background: "#E9B9FF",
          width: `${audioLevel * 100}%`,
        }}></div>
      </div>
      <div style={{ fontSize: "11px", marginTop: "10px", lineHeight: "1.2em" }}>
        You should see a moving purple bar. If not, fix your audio settings or your browser permissions. <br/><br/> You can mute or unmute any time.
      </div>
    </div>
  );
};


const StyledRouletteModal = styled("div", {
  width: "100vw",
  height: vh(100),
  position: "fixed",
  top: 0,
  left: 0,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  backgroundColor: "rgba(180, 180, 180, 0.4)",
  zIndex: 999999,
  pointerEvents: "none",
  
  "& .modal": {
    backgroundColor: "$wash",
    width: "280px",
    padding: "40px 40px",
    borderRadius: "20px",
    border: "1px solid black",
    height: "fit-content",
    position: "relative",
    textAlign: "center",
    pointerEvents: "auto",

    "& .title": {
      fontSize: "13px",
      fontWeight: "500",
      marginBottom: "14px",
    },
    "& .subtitle": {
      fontSize: "12px",
      marginBottom: "16px",
      lineHeight: "1.4",
    },
  },
  
  variants: {
    Theme: {
      dark: {
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        "& .modal": {
          backgroundColor: "$darkwash",
          color: "white",
          border: "1px solid $gray600",
        }
      },
      light: {},
    },
  },
});
